/**
 * useTableSort — reusable hook untuk sortable table dengan:
 *   - 3-state cycle per kolom (asc → desc → reset)
 *   - Multi-sort via Shift+click (push ke chain, drop kalau sudah ada)
 *   - Persistent ke localStorage per `storageKey`
 *
 * Usage:
 *   const { sortBy, handleSort, SortHeader, applySorts } = useTableSort({
 *     storageKey: "review-queue-sort",
 *     accessor: (rec, key) => { ... },
 *   });
 *   const sortedRecords = applySorts(filteredRecords);
 *   <SortHeader k="amount" label="Nominal" align="right" />
 *
 * State shape: array of { key, dir: "asc"|"desc" }. Empty = no sort.
 */
import { useState, useEffect, useCallback } from "react";
import { CaretUp, CaretDown, ArrowsDownUp } from "@phosphor-icons/react";

export function useTableSort({ storageKey, accessor, testIdPrefix = "sort-header" }) {
  const [sortBy, setSortBy] = useState(() => {
    if (!storageKey) return [];
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      // Backward-compat: tolerate legacy single-object format
      if (Array.isArray(parsed)) return parsed;
      if (parsed && parsed.key) return [parsed];
      return [];
    } catch {
      return [];
    }
  });

  // Persist on change
  useEffect(() => {
    if (!storageKey) return;
    try {
      if (sortBy.length === 0) localStorage.removeItem(storageKey);
      else localStorage.setItem(storageKey, JSON.stringify(sortBy));
    } catch { /* ignore quota errors */ }
  }, [sortBy, storageKey]);

  /**
   * Toggle sort untuk key. Multi-sort kalau shiftKey=true.
   * Single-sort behavior: cycle asc → desc → none (clear all).
   * Multi-sort behavior: tambah ke chain (asc → desc → drop).
   */
  const handleSort = useCallback((key, shiftKey = false) => {
    setSortBy(prev => {
      const idx = prev.findIndex(s => s.key === key);
      const existing = idx >= 0 ? prev[idx] : null;

      if (!shiftKey) {
        // Single-sort: replace seluruh chain
        if (!existing) return [{ key, dir: "asc" }];
        if (existing.dir === "asc") return [{ key, dir: "desc" }];
        return []; // clear
      }

      // Multi-sort: chain modification
      if (!existing) return [...prev, { key, dir: "asc" }];
      const next = [...prev];
      if (existing.dir === "asc") {
        next[idx] = { key, dir: "desc" };
      } else {
        next.splice(idx, 1); // drop from chain
      }
      return next;
    });
  }, []);

  const clearSort = useCallback(() => setSortBy([]), []);

  /**
   * Apply current sortBy chain ke array. Empty values selalu di akhir.
   */
  const applySorts = useCallback((arr) => {
    if (!sortBy.length || !arr.length) return arr;
    const sorters = sortBy.map(s => ({
      key: s.key,
      mult: s.dir === "asc" ? 1 : -1,
    }));
    return [...arr].sort((a, b) => {
      for (const { key, mult } of sorters) {
        const va = accessor(a, key);
        const vb = accessor(b, key);
        const isEmptyA = va === "" || va === null || va === undefined;
        const isEmptyB = vb === "" || vb === null || vb === undefined;
        if (isEmptyA && !isEmptyB) return 1;
        if (!isEmptyA && isEmptyB) return -1;
        if (isEmptyA && isEmptyB) continue;
        if (va < vb) return -1 * mult;
        if (va > vb) return 1 * mult;
      }
      return 0;
    });
  }, [sortBy, accessor]);

  /**
   * Header component.
   * Tampilkan: arah (caret up/down) dan urutan dalam chain (badge angka kalau >1 kolom aktif).
   */
  const SortHeader = useCallback(({ k, label, align = "left" }) => {
    const idx = sortBy.findIndex(s => s.key === k);
    const active = idx >= 0;
    const dir = active ? sortBy[idx].dir : null;
    const Icon = !active ? ArrowsDownUp : (dir === "asc" ? CaretUp : CaretDown);
    const isMulti = sortBy.length > 1;
    return (
      <th className={`text-${align} px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] cursor-pointer select-none ${active ? "text-zinc-900" : "text-zinc-500 hover:text-zinc-700"}`}
        onClick={(e) => handleSort(k, e.shiftKey)}
        data-testid={`${testIdPrefix}-${k}`}
        title={active
          ? `Sort ${dir === "asc" ? "A→Z" : "Z→A"} (klik = ${dir === "asc" ? "Z→A" : "reset"} · Shift+klik untuk multi-sort)`
          : `Klik untuk sort by ${label} · Shift+klik untuk multi-sort`}>
        <span className={`inline-flex items-center gap-1 ${align === "right" ? "flex-row-reverse" : ""}`}>
          {label}
          <Icon size={10} weight={active ? "bold" : "regular"} className={active ? "text-violet-600" : "text-zinc-300"} />
          {active && isMulti && (
            <span className="text-[8px] font-mono bg-violet-100 text-violet-700 rounded px-1 leading-none py-0.5"
              data-testid={`${testIdPrefix}-${k}-order`}>
              {idx + 1}
            </span>
          )}
        </span>
      </th>
    );
  }, [sortBy, handleSort, testIdPrefix]);

  return { sortBy, handleSort, clearSort, SortHeader, applySorts };
}
